/*
 * Copyright (c) Nexign, JSC, 2022-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.19.0
 */

import { createSelector } from 'reselect';

import type { IRootReducer } from '../../reducers/rootReducer';
import { makeSelectTranslationCommon } from '../internationalization/intSelectors';
import type { ITableButton } from './api';
import { tableSlice } from './slice';
import { tableApi } from './store';
import type { TTableColumnCell, TTableColumnConfig } from './types';
import { clearTableMetaFilters } from './utils';

/* Table Endpoints Selector */
export const tableMetaSelector = (
    url?: string, 
    context?: string,
) => tableApi.endpoints.getTableMeta.select({ url: url || '', context });

export const tableSliceSelector = (state: IRootReducer) => state[tableSlice.name];

export const tableMetaUrlSelector = createSelector(tableSliceSelector, ({ meta }) => meta?.url);

export const tableListUrlSelector = createSelector(tableSliceSelector, ({ list }) => list?.url);

export const tableListIsTemplateSelector = createSelector(tableSliceSelector, ({ list }) => list?.isTemplate);

export const tableFilterUrlSelector = createSelector(tableSliceSelector, ({ filterAndSort }) => filterAndSort?.url);
export const tableFilterSuggestUrlSelector = createSelector(
    tableSliceSelector,
    ({ filterAndSort }) => filterAndSort?.suggest,
);
export const tableFilterPresetUrlSelector = createSelector(
    tableSliceSelector,
    ({ filterAndSort }) => filterAndSort?.preset,
);

export const tableBasketCheckboxUrlSelector = createSelector(tableSliceSelector, ({ basket }) => basket?.checkbox);
export const tableBasketListUrlSelector = createSelector(tableSliceSelector, ({ basket }) => basket?.list);
export const tableBasketGtmListSelector = createSelector(tableSliceSelector, ({ basket }) => basket?.gtm?.loadList);

export const tableResizeUrlSelector = createSelector(tableSliceSelector, ({ resize }) => resize?.url);

export const tableColumnSettingsUrlSelector = createSelector(
    tableSliceSelector,
    ({ columnSettings }) => columnSettings?.url,
);

export const tableLabelsUrlSelector = createSelector(tableSliceSelector, ({ labels }) => labels?.url);
export const tableLabelsFieldNameSelector = createSelector(tableSliceSelector, ({ labels }) => labels?.fieldName);

export const tableMetaFilterSelector = (meta?: string, availableNames?: string[], context?: string) =>
    createSelector(tableMetaSelector(meta, context), (meta) =>
        clearTableMetaFilters(meta.data?.filterAndSort?.filter ?? {}, availableNames),
    );

export const tableMetaFieldsSelector = (meta?: string, context?: string) =>
    createSelector(tableMetaSelector(meta, context), (meta) => meta.data?.fields ?? []);

export const tableMetaFieldsDraggableSelector = (meta?: string, context?: string) =>
    createSelector(tableMetaFieldsSelector(meta, context), (fields) =>
        fields.filter((field) => ['S', 'M'].includes(field.type)),
    );

export const tableMetaFieldsRequiredSelector = (meta?: string, context?: string) =>
    createSelector(tableMetaFieldsSelector(meta, context), (fields) => fields.filter((field) => ['C'].includes(field.type)));

export const tableMetaColumnsSelector = <D extends object>(
    columnsConfig: TTableColumnConfig<D>,
    meta?: string,
    context?: string,
    cellSizeConfig?: Record<string, number>,
) =>
    createSelector(tableMetaFieldsSelector(meta, context), (fields) => {
        const columns: Array<TTableColumnCell<D>> = fields
            // remove hidden columns
            .filter((col) => col.checked && !!columnsConfig[col.name])
            // add params from UI config
            .map((col) => ({
                width: cellSizeConfig ? cellSizeConfig[col.name] : col.size,
                ...columnsConfig[col.name],
            }));

        // add checkbox column (if exist)
        if (columnsConfig.checked) {
            columns.unshift(columnsConfig.checked);
        }

        return columns;
    });

export const tableMetaSortSelector = (meta?: string) =>
    createSelector(tableMetaSelector(meta), (meta) => meta.data?.filterAndSort?.sort);

export const tableMetaFacetsSelector = (meta?: string, context?: string) =>
    createSelector(tableMetaSelector(meta, context), (meta) => meta.data?.facets ?? {});

export const tableMetaExportSelector = (meta?: string) =>
    createSelector(tableMetaSelector(meta), (meta) => meta.data?.exportExtensions ?? []);

export const tableMetaBasketAllInSelector = (meta?: string) =>
    createSelector(tableMetaSelector(meta), (meta) => meta.data?.basketAllIn ?? false);

export const tableMetaButtonsSelector = (meta?: string) =>
    createSelector(tableMetaSelector(meta), (meta) => meta.data?.buttons || []);

export const tableMetaButtonsEnabledSelector = (meta?: string) =>
    createSelector(tableMetaButtonsSelector(meta), (buttons) =>
        buttons.reduce((buttons: { [buttonName: string]: boolean }, button: ITableButton) => {
            buttons[button.buttonName] = true;
            return buttons;
        }, {}),
    );

/* For Field */

export const tableMetaFieldSelector = (meta?: string, fieldName?: string, context?: string) =>
    createSelector(
        tableMetaFieldsSelector(meta, context), (fields) => fields.find((field) => field.name === fieldName),
    );

export const tableFiledFacetSelector = (meta?: string, fieldName?: string, context?: string) =>
    createSelector(tableMetaFacetsSelector(meta, context), (facets) => facets[fieldName || '']);

export const tableFiledFilterSelector = (meta?: string, fieldName?: string, context?: string) =>
    createSelector(tableMetaFilterSelector(meta, undefined, context), (filter) => filter[fieldName || '']);

/* Labels */

export const tableLabelsSelector = (labelsUrl: string) => tableApi.endpoints.getLabelsList.select({ url: labelsUrl });

export const tableLabelsListSelector = (labelsUrl?: string) =>
    createSelector(tableLabelsSelector(labelsUrl || ''), (labels) => labels.data ?? []);

/* Table Data */

export const tableDataSelector = createSelector(tableSliceSelector, ({ tableData }) => tableData);

export const tableDataHasMoreSelector = createSelector(tableSliceSelector, ({ tableDataHasMore }) => tableDataHasMore);

export const tableDataCountSelector = createSelector(tableSliceSelector, ({ tableDataCount }) => tableDataCount);

export const tableDataLoadSizeSelector = createSelector(tableSliceSelector, ({ tableLoadSize }) => tableLoadSize);

export const tableSearchAfterValuesSelector = createSelector(
    tableSliceSelector,
    ({ tableSearchAfterValues }) => tableSearchAfterValues,
);

/* statuses */

export const tableIsLoadingSelector = createSelector(tableSliceSelector, ({ requestStatus }) => {
    if (!requestStatus.isInitialised) {
        return true;
    }
    return requestStatus.meta.isLoading || requestStatus.list.isLoading || requestStatus.filterAndSort.isLoading;
});

export const tableIsErrorSelector = createSelector(
    tableSliceSelector,
    ({ requestStatus }) =>
        requestStatus.meta.isError || requestStatus.list.isError || requestStatus.filterAndSort.isError,
);

export const tableIsLoadedWithDataSelector = createSelector(
    tableSliceSelector,
    ({ tableData }) => Array.isArray(tableData) && tableData.length > 0,
);

/* Basket */
export const tableFastBasketAllInSelector = 
    createSelector(tableSliceSelector, ({ fastBasketAllIn }) => fastBasketAllIn);

export const tableBasketAllInSelector = createSelector(tableSliceSelector, ({ basketAllIn }) => basketAllIn);

export const tableBasketDataSelector = createSelector(tableSliceSelector, ({ basketData }) => basketData);

export const tableBasketHasMoreSelector = createSelector(tableSliceSelector, ({ basketHasMore }) => basketHasMore);

export const tableBasketCountSelector = createSelector(tableSliceSelector, ({ basketCount }) => basketCount || 0);

export const tableBasketParamsSelector = createSelector(tableSliceSelector, ({ basketParams }) => basketParams);

export const tableButtonsModeSelector = createSelector(tableSliceSelector, ({ buttonsMode }) => buttonsMode);

/* Translation */

export const tableTranslationNoValueSelector = createSelector(
    makeSelectTranslationCommon(),
    ({ corporateTable }) => corporateTable.noValue,
);

/* Subscribers in roaming */

export const tableSubscribersInRoamingCount = 
    createSelector(tableSliceSelector, ({ subscribersInRoamingCount }) => subscribersInRoamingCount ?? 0);

export const tableSubscribersInRoamingWithFilterCount = 
    createSelector(
        tableSliceSelector, ({ subscribersInRoamingWithFilterCount }) => subscribersInRoamingWithFilterCount ?? 0,
    );

export const tableFilterSubscribersInRoaming = 
    createSelector(tableSliceSelector, ({ filterSubscribersInRoaming }) => filterSubscribersInRoaming ?? false);


/* For the fast action pages */

export const tableContext =
    createSelector(tableSliceSelector, ({ context }) => context);

export const tableIsFastActionPage = 
    createSelector(tableSliceSelector, ({ isFastActionPage }) => isFastActionPage);

export const tableIsFastBasketPage =
    createSelector(tableSliceSelector, ({ isFastBasketPage }) => isFastBasketPage);
